import { useEffect, useRef, useState } from 'react'
import optionService from '../services/optionService'
import leadService from '../services/leadService'
import settingService, { toMap } from '../services/settingService'
import menuService from '../services/menuService'
import { getTracking } from '../utils/tracking'
import toast from 'react-hot-toast'
import {
  HiOutlineUser, HiOutlinePhone, HiOutlineMail, HiOutlineCalendar,
  HiOutlineLocationMarker, HiOutlineBriefcase, HiOutlineTag,
  HiOutlineCloudUpload, HiOutlineCheckCircle, HiOutlineArrowRight, HiOutlinePaperAirplane,
  HiOutlineMenu, HiOutlineX, HiOutlineDocumentText, HiOutlineShieldCheck,
  HiOutlineOfficeBuilding, HiOutlineGlobeAlt, HiOutlineChevronDown,
} from 'react-icons/hi'

const BVP = { fontFamily: "'Be Vietnam Pro', system-ui, sans-serif" }
const NAVY = '#14366f'   // màu navy tiêu đề (2 dòng cùng màu như design)

// Fallback CMS (có sẵn ngắt dòng đúng mẫu)
const FALLBACK = {
  brand_logo: '/logo-dxmt.png',
  brand_name_short: 'Đất Xanh Miền Trung',
  hero_eyebrow: 'Gia nhập',
  hero_title: 'ĐẤT XANH MIỀN TRUNG',
  hero_subtitle: 'Nơi sự nghiệp của bạn bắt đầu\nvà tỏa sáng cùng những giá trị bền vững.',
  hero_cta: 'Xem vị trí tuyển dụng',
  contact_hotline: '0932224433',
  contact_email: 'tuyendung@datxanhmientrung.com',
  contact_website: 'https://datxanhmientrung.com/',
  contact_address_main: '386 Điện Biên Phủ, Thanh Khê, Đà Nẵng',
  footer_cta_title: 'SẴN SÀNG ĐỒNG HÀNH\nCÙNG ĐẤT XANH MIỀN TRUNG?',
  footer_cta_subtitle: 'Hãy gửi hồ sơ ngay hôm nay để trở thành một phần\ncủa đội ngũ năng động và chuyên nghiệp.',
}

const FALLBACK_NAV = [
  { id: 'f1', label: 'Về chúng tôi', url: 'https://datxanhmientrung.com/', target: '_blank', children: [] },
  { id: 'f2', label: 'Dự án', url: 'https://datxanhmientrung.com/', target: '_blank', children: [] },
  { id: 'f3', label: 'Tin tức', url: 'https://datxanhmientrung.com/', target: '_blank', children: [] },
  { id: 'f4', label: 'Tuyển dụng', url: '#form', target: '_self', children: [] },
  { id: 'f5', label: 'Liên hệ', url: '#contact', target: '_self', children: [] },
]

const splitTitle = (title = '') => {
  const w = title.trim().split(/\s+/).filter(Boolean)
  if (w.length <= 1) return [title, '']
  const mid = Math.ceil(w.length / 2)
  return [w.slice(0, mid).join(' '), w.slice(mid).join(' ')]
}

// Tách "Tên | Địa chỉ" mỗi dòng -> [{name, address}] (dùng cho Hệ thống văn phòng)
const parseAddresses = (raw = '') =>
  raw.split('\n').map((l) => l.trim()).filter(Boolean).map((line) => {
    const [name, ...rest] = line.split('|')
    return { name: name.trim(), address: rest.join('|').trim() }
  })

// Sparkle ✦ trang trí
const Sparkle = ({ className = '' }) => (
  <svg viewBox="0 0 24 24" className={className} fill="#93C5FD" aria-hidden>
    <path d="M12 0 L14 10 L24 12 L14 14 L12 24 L10 14 L0 12 L10 10 Z" />
  </svg>
)

// ─── Logo ─────────────────────────────────────────────────────────
function Logo({ src, alt }) {
  return (
    <a href="#top" className="flex items-center gap-2 shrink-0">
      <img src={src || FALLBACK.brand_logo} alt={alt || 'Logo'} className="h-9 sm:h-10 w-auto" />
    </a>
  )
}

function NavItem({ item, onNavigate }) {
  const active = (item.url || '').includes('#form')
  if (item.children?.length) {
    return (
      <div className="relative group">
        <button className="inline-flex items-center gap-1 text-sm font-medium text-secondary-600 hover:text-brand-500 transition-colors">
          {item.label} <HiOutlineChevronDown className="w-4 h-4" />
        </button>
        <div className="absolute left-0 top-full pt-2 hidden group-hover:block min-w-[200px] z-50">
          <div className="bg-white rounded-xl shadow-soft border border-secondary-100 py-2">
            {item.children.map((c) => (
              <a key={c.id} href={c.url} target={c.target} onClick={onNavigate}
                rel={c.target === '_blank' ? 'noreferrer' : undefined}
                className="block px-4 py-2 text-sm text-secondary-600 hover:bg-brand-50 hover:text-brand-500">
                {c.label}
              </a>
            ))}
          </div>
        </div>
      </div>
    )
  }
  return (
    <a href={item.url} target={item.target} onClick={onNavigate}
      rel={item.target === '_blank' ? 'noreferrer' : undefined}
      className={`text-sm font-medium transition-colors pb-1 ${active ? 'text-brand-500 border-b-2 border-brand-500' : 'text-secondary-600 hover:text-brand-500'}`}>
      {item.label}
    </a>
  )
}

// ─── Header ───────────────────────────────────────────────────────
function Header({ s, nav }) {
  const [open, setOpen] = useState(false)
  const close = () => setOpen(false)
  return (
    <header className="sticky top-0 z-40 bg-white/95 backdrop-blur border-b border-secondary-100">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-4">
        <Logo src={s.brand_logo} alt={s.brand_name_short} />
        <nav className="hidden lg:flex items-center gap-8">
          {nav.map((n) => <NavItem key={n.id} item={n} />)}
        </nav>
        <div className="flex items-center gap-2">
          <a href="#form" className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg border-2 border-brand-500 text-brand-500 font-semibold text-sm hover:bg-brand-50 transition-colors">
            <HiOutlineUser className="w-4 h-4" /> Gia nhập ngay
          </a>
          <button onClick={() => setOpen((v) => !v)} className="lg:hidden p-2 -mr-2 text-secondary-700" aria-label="menu">
            {open ? <HiOutlineX className="w-6 h-6" /> : <HiOutlineMenu className="w-6 h-6" />}
          </button>
        </div>
      </div>
      {open && (
        <div className="lg:hidden border-t border-secondary-100 bg-white px-4 py-3 space-y-1">
          {nav.map((n) => (
            <a key={n.id} href={n.url} target={n.target} onClick={close}
              rel={n.target === '_blank' ? 'noreferrer' : undefined}
              className="block px-2 py-2 rounded-lg text-sm font-medium text-secondary-700 hover:bg-brand-50">
              {n.label}
            </a>
          ))}
          <a href="#form" onClick={close} className="block px-2 py-2 rounded-lg text-sm font-semibold text-brand-500">Gia nhập ngay →</a>
        </div>
      )}
    </header>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────
function Hero({ s }) {
  const [l1, l2] = splitTitle(s.hero_title)
  return (
    <section id="top" className="relative overflow-hidden bg-white">
      {/* Nền hero render sẵn (chấm + sóng + vòng tròn xanh + chart mờ) */}
      <div className="absolute inset-0 bg-cover bg-center pointer-events-none"
        style={{ backgroundImage: 'url(/dxmt/bg/hero-bg-abstract.png)' }} aria-hidden />
      {/* Ảnh team full-bleed bên phải (ảnh tự có viền mờ trắng trái, KHÔNG khung) */}
      <img src="/dxmt/img/team-meeting.png" alt="Đội ngũ Đất Xanh Miền Trung" aria-hidden
        className="hidden lg:block absolute top-0 right-0 h-full w-[54%] object-cover object-center pointer-events-none select-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-12 lg:py-24 lg:min-h-[540px]">
        <div className="lg:w-[46%]">
          <p className="text-3xl sm:text-4xl font-semibold text-brand-500 mb-1">{s.hero_eyebrow}</p>
          <h1 className="text-5xl sm:text-6xl lg:text-[64px] font-extrabold uppercase" style={{ color: NAVY, lineHeight: 1.15 }}>
            {l1}{l2 && <><br />{l2}</>}
          </h1>
          <p className="mt-5 text-base sm:text-lg text-secondary-600 whitespace-pre-line">{s.hero_subtitle}</p>
          <a href="#form"
            className="mt-7 inline-flex items-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-tr from-[#F96200] to-[#FE9016] text-white font-semibold shadow-lg shadow-accent-500/30 transition hover:brightness-[1.06]">
            {s.hero_cta} <HiOutlineArrowRight className="w-5 h-5" />
          </a>
        </div>

        {/* Ảnh cho mobile */}
        <div className="lg:hidden mt-8 -mx-4 sm:-mx-6">
          <img src="/dxmt/img/team-meeting.png" alt="Đội ngũ Đất Xanh Miền Trung" className="w-full" />
        </div>
      </div>
    </section>
  )
}

// ─── Benefits ─────────────────────────────────────────────────────
const BENEFITS = [
  { icon: '/dxmt/icons/icon-income-growth.svg', title: 'Thu nhập hấp dẫn', desc: ['Chính sách thưởng', 'rõ ràng, xứng đáng'] },
  { icon: '/dxmt/icons/icon-training.svg', title: 'Đào tạo bài bản', desc: ['Kèm cặp thực chiến,', 'phát triển toàn diện'] },
  { icon: '/dxmt/icons/icon-team.svg', title: 'Môi trường năng động', desc: ['Văn hóa tích cực,', 'đề cao sự đồng đội'] },
  { icon: '/dxmt/icons/icon-promotion.svg', title: 'Cơ hội thăng tiến', desc: ['Lộ trình phát triển rõ ràng,', 'cơ hội rộng mở'] },
]

function Benefits() {
  return (
    <section id="benefits" className="max-w-7xl mx-auto px-4 sm:px-6 -mt-6 relative z-10">
      <div className="bg-white rounded-2xl shadow-soft border border-secondary-100 p-6 sm:p-8 grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-8">
        {BENEFITS.map((b) => (
          <div key={b.title} className="text-center px-2">
            <img src={b.icon} alt="" aria-hidden className="w-16 h-16 mx-auto mb-3" />
            <h3 className="font-bold text-brand-500 text-sm sm:text-base">{b.title}</h3>
            <p className="text-xs sm:text-sm text-secondary-500 mt-1 leading-relaxed">{b.desc[0]}<br />{b.desc[1]}</p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Criteria (icon trắng trên tròn xanh đặc) ─────────────────────
const ICON_TARGET = (<><circle cx="48" cy="48" r="24" /><circle cx="48" cy="48" r="12" /><path d="M48 24V15" /><path d="M48 81V72" /><path d="M24 48H15" /><path d="M81 48H72" /></>)
const ICON_CONNECT = (<><circle cx="34" cy="39" r="8" /><circle cx="62" cy="39" r="8" /><path d="M26 68C27 56 42 54 48 61" /><path d="M48 61C54 54 69 56 70 68" /><path d="M42 39H54" /></>)
const ICON_BOOK = (<><path d="M30 28H62C66 28 68 31 68 35V68C68 70 66 70 64 69C55 65 41 65 32 69C30 70 28 69 28 67V33C28 30 29 28 30 28Z" /><path d="M48 31V66" /><path d="M36 42H43" /><path d="M53 42H60" /></>)
const ICON_BULB = (<><path d="M48 24V36" /><path d="M35 29L42 39" /><path d="M61 29L54 39" /><path d="M32 47C32 38 64 38 64 47C64 57 55 62 48 70C41 62 32 57 32 47Z" /><path d="M42 53H54" /></>)
const ICON_STEPS = (<><path d="M30 68H66" /><path d="M35 64V55H46V46H57V37H68" /><path d="M68 37H58" /><path d="M68 37V47" /></>)

const CRITERIA = [
  { icon: ICON_TARGET, title: ['Khát khao', 'thu nhập cao'], desc: ['Sẵn sàng chinh phục', 'mục tiêu và bứt phá', 'giới hạn bản thân.'] },
  { icon: ICON_CONNECT, title: ['Yêu thích', 'kết nối'], desc: ['Tự tin giao tiếp, xây dựng', 'mối quan hệ và tạo giá trị', 'cho khách hàng.'] },
  { icon: ICON_BOOK, title: ['Tinh thần', 'học hỏi'], desc: ['Luôn chủ động nâng cấp', 'kỹ năng và tiếp thu cái mới', 'mỗi ngày.'] },
  { icon: ICON_BULB, title: ['Tư duy', 'chủ động'], desc: ['Dám hành động, dám', 'chịu trách nhiệm và không', 'ngại thử thách.'] },
  { icon: ICON_STEPS, title: ['Mong muốn', 'thăng tiến'], desc: ['Tìm kiếm một môi trường', 'có lộ trình phát triển', 'rõ ràng và bền vững.'] },
]

function Criteria() {
  return (
    <section id="criteria" className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 sm:py-20">
      <Sparkle className="hidden md:block absolute left-6 top-24 w-5 h-5" />
      <Sparkle className="hidden md:block absolute right-24 top-32 w-4 h-4 opacity-70" />
      <img src="/dxmt/patterns/dot-pattern.svg" alt="" aria-hidden className="hidden md:block absolute top-16 right-2 w-24 opacity-40" />
      <img src="/dxmt/patterns/dot-pattern.svg" alt="" aria-hidden className="hidden md:block absolute bottom-10 left-0 w-16 opacity-30" />

      <div className="text-center mb-10">
        <h2 className="text-2xl sm:text-[32px] font-extrabold" style={{ color: NAVY }}>
          BẠN PHÙ HỢP VỚI ĐẤT XANH MIỀN TRUNG NẾU…
        </h2>
        <div className="h-1 w-16 bg-accent-500 rounded-full mx-auto mt-3" />
        <p className="mt-4 text-secondary-500">
          Không chỉ là công việc, đây là nơi dành cho những người muốn bứt phá sự nghiệp.
        </p>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-5">
        {CRITERIA.map((c) => (
          <div key={c.title.join()} className="bg-white rounded-2xl border border-secondary-100 shadow-card hover:shadow-card-hover hover:-translate-y-1 transition-all p-5 pt-7 text-center">
            <div className="w-[76px] h-[76px] mx-auto rounded-full bg-brand-500 flex items-center justify-center shadow-lg shadow-brand-500/25">
              <svg viewBox="0 0 96 96" className="w-11 h-11" fill="none" stroke="white" strokeWidth="5" strokeLinecap="round" strokeLinejoin="round">
                {c.icon}
              </svg>
            </div>
            <h3 className="mt-4 font-bold leading-tight" style={{ color: NAVY }}>{c.title[0]}<br />{c.title[1]}</h3>
            <p className="mt-2 text-xs text-secondary-500 leading-snug">
              {c.desc.map((d, i) => <span key={i}>{d}{i < c.desc.length - 1 && <br />}</span>)}
            </p>
          </div>
        ))}
      </div>
    </section>
  )
}

// ─── Form ─────────────────────────────────────────────────────────
function Field({ icon: Icon, label, children }) {
  return (
    <div>
      <label className="flex items-center gap-1.5 text-sm font-medium text-secondary-700 mb-1.5">
        <Icon className="w-4 h-4 text-brand-500" />
        {label} <span className="text-red-500">*</span>
      </label>
      {children}
    </div>
  )
}

const ALLOWED_EXT = ['pdf', 'doc', 'docx']
const MAX_SIZE = 5 * 1024 * 1024
const emptyForm = {
  full_name: '', phone: '', email: '', birth_year: '', current_location: '',
  desired_area: '', experience: '', position: '', source: '', cv_url: '', cv_filename: '', note: '',
}

function ApplySection({ brandShort }) {
  const [form, setForm] = useState(emptyForm)
  const [options, setOptions] = useState({ position: [], work_area: [], experience: [], source: [] })
  const [submitting, setSubmitting] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [done, setDone] = useState(false)
  const fileRef = useRef(null)
  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }))

  useEffect(() => {
    optionService.getPublic()
      .then((res) => { if (res.status === 'success') setOptions((p) => ({ ...p, ...res.data })) })
      .catch(() => {})
  }, [])

  const handleFile = async (file) => {
    if (!file) return
    const ext = file.name.split('.').pop().toLowerCase()
    if (!ALLOWED_EXT.includes(ext)) { toast.error('Chỉ chấp nhận file PDF, DOC, DOCX'); return }
    if (file.size > MAX_SIZE) { toast.error('File vượt quá 5MB'); return }
    setUploading(true)
    try {
      const res = await leadService.uploadCv(file)
      if (res.status === 'success') { set('cv_url', res.data.url); set('cv_filename', res.data.filename); toast.success('Đã tải CV lên') }
    } catch { /* interceptor */ } finally { setUploading(false) }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    const phone = form.phone.trim(), email = form.email.trim(), year = form.birth_year.trim()
    if (!form.full_name.trim()) { toast.error('Vui lòng nhập Họ và tên'); return }
    if (!/^0\d{9,10}$/.test(phone)) { toast.error('Số điện thoại không hợp lệ (10–11 số, bắt đầu bằng 0)'); return }
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { toast.error('Email không hợp lệ'); return }
    if (year && !/^(19|20)\d{2}$/.test(year)) { toast.error('Năm sinh không hợp lệ (VD: 1995)'); return }
    setSubmitting(true)
    try {
      const res = await leadService.submit({ ...form, source: form.source || undefined, tracking: getTracking() })
      if (res.status === 'success') setDone(true)
    } catch { /* interceptor */ } finally { setSubmitting(false) }
  }

  if (done) {
    return (
      <section id="form" className="max-w-2xl mx-auto px-4 sm:px-6 py-16">
        <div className="card p-8 text-center">
          <HiOutlineCheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-secondary-900 mb-2">Gửi hồ sơ thành công!</h2>
          <p className="text-secondary-500 mb-6">Cảm ơn bạn đã ứng tuyển vào {brandShort || 'Đất Xanh Miền Trung'}. Bộ phận tuyển dụng sẽ liên hệ với bạn sớm nhất.</p>
          <button onClick={() => { setForm(emptyForm); setDone(false) }} className="btn-outline">Gửi hồ sơ khác</button>
        </div>
      </section>
    )
  }

  const renderOptions = (cat) => options[cat]?.map((o) => <option key={o.id} value={o.value}>{o.label}</option>)

  return (
    <section id="form" className="relative bg-[#F5F9FF] py-16 sm:py-20 overflow-hidden">
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6">
        <div className="mb-8">
          <h2 className="text-2xl sm:text-3xl font-extrabold" style={{ color: NAVY }}>ĐIỀN FORM ỨNG TUYỂN</h2>
          <div className="h-1 w-20 bg-accent-500 rounded-full mt-2" />
        </div>

        <form onSubmit={handleSubmit} className="relative overflow-hidden bg-white rounded-2xl border border-secondary-100 shadow-sm p-6 sm:p-8">
          <img src="/dxmt/patterns/dot-pattern.svg" alt="" aria-hidden className="absolute top-4 right-4 w-20 opacity-40 pointer-events-none" />
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-5">
            <Field icon={HiOutlineUser} label="Họ và tên">
              <input className="input" value={form.full_name} onChange={(e) => set('full_name', e.target.value)} placeholder="Nhập họ và tên" />
            </Field>
            <Field icon={HiOutlinePhone} label="Số điện thoại">
              <input className="input" value={form.phone} inputMode="tel" maxLength={11}
                onChange={(e) => set('phone', e.target.value.replace(/[^\d]/g, ''))} placeholder="Nhập số điện thoại" />
            </Field>
            <Field icon={HiOutlineMail} label="Email">
              <input type="email" className="input" value={form.email} onChange={(e) => set('email', e.target.value)} placeholder="Nhập email của bạn" />
            </Field>

            <Field icon={HiOutlineCalendar} label="Năm sinh">
              <input className="input" value={form.birth_year} inputMode="numeric" maxLength={4}
                onChange={(e) => set('birth_year', e.target.value.replace(/[^\d]/g, ''))} placeholder="VD: 1995" />
            </Field>
            <Field icon={HiOutlineLocationMarker} label="Nơi ở hiện tại">
              <input className="input" value={form.current_location} onChange={(e) => set('current_location', e.target.value)} placeholder="Nhập nơi ở hiện tại" />
            </Field>
            <Field icon={HiOutlineLocationMarker} label="Khu vực làm việc mong muốn">
              <input className="input" value={form.desired_area} onChange={(e) => set('desired_area', e.target.value)} placeholder="Nhập khu vực mong muốn" />
            </Field>

            <Field icon={HiOutlineBriefcase} label="Kinh nghiệm">
              <select className="select" value={form.experience} onChange={(e) => set('experience', e.target.value)}>
                <option value="">Chọn kinh nghiệm</option>{renderOptions('experience')}
              </select>
            </Field>
            <Field icon={HiOutlineTag} label="Vị trí ứng tuyển">
              <select className="select" value={form.position} onChange={(e) => set('position', e.target.value)}>
                <option value="">Chọn vị trí phù hợp</option>{renderOptions('position')}
              </select>
            </Field>
            <Field icon={HiOutlineDocumentText} label="Tải CV (PDF, DOC, DOCX)">
              <div onClick={() => !uploading && fileRef.current?.click()}
                onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); handleFile(e.dataTransfer.files?.[0]) }}
                className="border-2 border-dashed border-secondary-200 rounded-lg px-3 py-2.5 text-center cursor-pointer hover:border-brand-400 hover:bg-brand-50/40 transition-colors">
                <input ref={fileRef} type="file" accept=".pdf,.doc,.docx" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} />
                {uploading ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-secondary-500 py-1"><span className="spinner w-4 h-4" /> Đang tải lên…</div>
                ) : form.cv_filename ? (
                  <div className="flex items-center justify-center gap-2 text-sm text-emerald-600 py-1"><HiOutlineCheckCircle className="w-5 h-5" /><span className="truncate max-w-[160px]">{form.cv_filename}</span></div>
                ) : (
                  <div className="flex flex-col items-center text-secondary-400 py-1">
                    <HiOutlineCloudUpload className="w-6 h-6 text-brand-400" />
                    <span className="text-xs mt-0.5">Kéo thả file hoặc click để tải lên</span>
                    <span className="text-[10px]">Tối đa 5MB</span>
                  </div>
                )}
              </div>
            </Field>
          </div>

          <p className="flex items-center gap-1.5 text-xs text-secondary-400 mt-5">
            <HiOutlineShieldCheck className="w-4 h-4" /> Thông tin của bạn được bảo mật và chỉ sử dụng cho mục đích tuyển dụng.
          </p>

          <div className="flex justify-center mt-6">
            <button type="submit" disabled={submitting || uploading}
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-xl bg-gradient-to-tr from-[#F96200] to-[#FE9016] text-white font-semibold shadow-lg shadow-accent-500/30 transition hover:brightness-[1.06] disabled:opacity-60">
              <HiOutlinePaperAirplane className="w-5 h-5 rotate-45" />
              {submitting ? 'Đang gửi…' : 'Gửi hồ sơ ứng tuyển'}
              <HiOutlineArrowRight className="w-5 h-5" />
            </button>
          </div>
        </form>
      </div>
    </section>
  )
}

// ─── Footer CTA ───────────────────────────────────────────────────
function FooterCTA({ s }) {
  const tel = (s.contact_hotline || '').replace(/\s/g, '')
  const offices = parseAddresses(s.contact_addresses)
  return (
    <footer id="contact" className="relative bg-brand-800 text-white overflow-hidden">
      <div className="absolute inset-0 bg-cover bg-center" style={{ backgroundImage: 'url(/dxmt/img/city-night.png)' }} />
      <div className="absolute inset-0 bg-gradient-to-r from-brand-900/90 via-brand-900/55 to-brand-900/10" />
      <div className="absolute inset-0 bg-gradient-to-t from-brand-900/90 via-transparent to-brand-900/25" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-14">
        <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6">
          <svg viewBox="0 0 420 220" className="hidden lg:block absolute left-[44%] -top-3 w-72 opacity-70 pointer-events-none" fill="none" aria-hidden>
            <path d="M20 180 C70 100 140 210 200 120 C250 45 300 125 360 70" stroke="white" strokeOpacity="0.5" strokeWidth="3" strokeDasharray="8 10" />
            <path d="M350 58 L405 36 L376 95 L365 70 L350 58Z" stroke="white" strokeWidth="3.5" strokeLinejoin="round" />
            <path d="M365 70 L405 36" stroke="white" strokeWidth="3.5" />
          </svg>
          <div className="max-w-lg">
            <h2 className="text-2xl sm:text-3xl font-extrabold leading-tight whitespace-pre-line">{s.footer_cta_title}</h2>
            <p className="mt-3 text-white/80 whitespace-pre-line">{s.footer_cta_subtitle}</p>
          </div>
          <div className="flex flex-col sm:flex-row lg:flex-col gap-3 shrink-0">
            <a href="#form" className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl bg-gradient-to-tr from-[#F96200] to-[#FE9016] font-semibold transition hover:brightness-[1.06] shadow-lg shadow-accent-500/30">
              {s.hero_cta || 'Xem vị trí tuyển dụng'} <HiOutlineArrowRight className="w-5 h-5" />
            </a>
            <a href={`tel:${tel}`} className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-xl border-2 border-white/70 font-semibold hover:bg-white/10 transition-colors">
              Liên hệ bộ phận tuyển dụng <HiOutlineArrowRight className="w-5 h-5" />
            </a>
          </div>
        </div>

        {/* Hệ thống văn phòng */}
        {offices.length > 0 && (
          <div className="mt-10 pt-8 border-t border-white/15">
            <h3 className="text-sm font-semibold uppercase tracking-wide text-white/70 mb-4">Hệ thống văn phòng</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {offices.map((o, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <HiOutlineOfficeBuilding className="w-5 h-5 text-accent-400 mt-0.5 shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-white">{o.name}</div>
                    <div className="text-sm text-white/75">{o.address}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Liên hệ nhanh */}
        <div className="mt-8 pt-6 border-t border-white/15 flex flex-col sm:flex-row flex-wrap gap-4 sm:gap-8 text-sm text-white/85">
          <a href={`tel:${tel}`} className="flex items-center gap-2 hover:text-white"><HiOutlinePhone className="w-5 h-5 text-accent-400" /> {s.contact_hotline}</a>
          <a href={`mailto:${s.contact_email}`} className="flex items-center gap-2 hover:text-white"><HiOutlineMail className="w-5 h-5 text-accent-400" /> {s.contact_email}</a>
          {s.contact_website && (
            <a href={s.contact_website} target="_blank" rel="noreferrer" className="flex items-center gap-2 hover:text-white">
              <HiOutlineGlobeAlt className="w-5 h-5 text-accent-400" /> {s.contact_website.replace(/^https?:\/\//, '').replace(/\/$/, '')}
            </a>
          )}
        </div>
      </div>
    </footer>
  )
}

export default function Landing() {
  const [s, setS] = useState(FALLBACK)
  const [nav, setNav] = useState(FALLBACK_NAV)

  useEffect(() => {
    settingService.list()
      .then((res) => { if (res.status === 'success' && res.data?.length) setS({ ...FALLBACK, ...toMap(res.data) }) })
      .catch(() => {})
    menuService.getPublic()
      .then((res) => { if (res.status === 'success' && res.data?.header?.length) setNav(res.data.header) })
      .catch(() => {})
  }, [])

  return (
    <div className="min-h-screen bg-white" style={BVP}>
      <Header s={s} nav={nav} />
      <Hero s={s} />
      <Benefits />
      <Criteria />
      <ApplySection brandShort={s.brand_name_short} />
      <FooterCTA s={s} />
    </div>
  )
}
